import React from 'react';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { Alert } from '@storable/canaveral-components';
import useIntegrations from '../../modules/react/hooks/useIntegrations';

/**
 * Get the distinct software providers from a collection of facilities.
 *
 * @param {Array} facilities - Array of facility objects.
 * @returns {Array}
 */
const getSoftwareProviders = (facilities) => {
  const softwareProviders = [];

  facilities.forEach((facility) => {
    const softwareProvider = facility?.settings?.softwareProvider;

    if (softwareProvider && !softwareProviders.includes(softwareProvider)) {
      softwareProviders.push(softwareProvider);
    }
  });

  return softwareProviders;
};

/**
 * Get pertinent alerts for the given provider(s).
 *
 * @param {Array} integrationsAlerting - Array of integrations with active alerts.
 * @param {Array} allProviders - Array of all providers present.
 * @param {Array} scopedProviders - Array of providers to further scope alerts. If
 * supplied, only alerts for providers in allProviders AND scopedProviders will
 * be returned.
 * @returns {Array}
 */
const getAlerts = (integrationsAlerting, allProviders, scopedProviders) => {
  const providers = scopedProviders.length > 0
    ? allProviders.filter(provider => scopedProviders.includes(provider))
    : allProviders;

  return integrationsAlerting
    .map(({ name, alert }) => {
      if (providers.includes(name)) {
        return alert;
      }
      return null;
    })
    .filter(Boolean);
};

/**
 * This HOC supplies pertinent integration alerts based on the passed Component's
 * facility or facilities.
 *
 * @param {Function|Object} Component - Component to be wrapped.
 * @param {String} message - Component-specific message to be displayed alongside
 * the integration alert, if any.
 * @param {Array} providers - Array of providers for scope. If supplied, only
 * alerts for integrations in this Array will be shown.
 * @returns
 */
const withAlert = ({
  Component,
  message = null,
  providers = [],
}) => {
  const ComponentWrapper = ({ facility, facilities, ...rest }) => {
    const { integrations } = useIntegrations();

    const integrationsAlerting = integrations.filter(({ alert }) => alert.active);

    if (integrationsAlerting.length === 0) {
      return (
        <Component
          {...rest}
          facility={facility}
          facilities={facilities}
        />
      );
    }

    const facilitiesToCheck = facility ? [facility, ...facilities] : facilities;
    const softwareProviders = getSoftwareProviders(facilitiesToCheck);
    const alerts = getAlerts(integrationsAlerting, softwareProviders, providers);

    const alertComponent = (
      <>
        {alerts.map(({ context, message: alertMessage }) => (
          <Alert context={context}>
            {message ? `${alertMessage} ${message}` : alertMessage}
          </Alert>
        ))}
      </>
    );

    return (
      <Component
        {...rest}
        alert={alertComponent}
        facilities={facilities}
        facility={facility}
      />
    );
  };

  ComponentWrapper.propTypes = {
    facility: PropTypes.shape({
      settings: PropTypes.shape({
        softwareProvider: PropTypes.string.isRequired,
      }).isRequired,
    }),
    facilities: PropTypes.arrayOf(PropTypes.shape({
      settings: PropTypes.shape({
        softwareProvider: PropTypes.string.isRequired,
      }).isRequired,
    })),
  };

  ComponentWrapper.defaultProps = {
    facility: undefined,
    facilities: [],
  };

  return hoistNonReactStatics(ComponentWrapper, Component);
};

export default withAlert;
